import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["status", "button"]
  
  connect() {
    this.checkLocationStatus()
  }
  
  checkLocationStatus() {
    if (navigator.geolocation) {
      // Check if we have permission
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        this.updateUI(result.state)
      }).catch(() => {
        this.updateUI('prompt')
      })
    } else {
      this.updateUI('not-supported')
    }
  }
  
  requestLocation() {
    if (!navigator.geolocation) {
      this.showError('Geolocation is not supported by this browser.')
      return
    }
    
    this.updateUI('loading')
    
    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 600000 // 10 minutes
    }
    
    navigator.geolocation.getCurrentPosition(
      (position) => this.handleSuccess(position),
      (error) => this.handleError(error),
      options
    )
  }
  
  handleSuccess(position) {
    const latitude = position.coords.latitude
    const longitude = position.coords.longitude
    
    console.log(`Location: ${latitude}, ${longitude}`)
    
    // Send to server
    fetch('/chat/update_location', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({
        latitude: latitude,
        longitude: longitude
      })
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        this.showSuccess(`Location updated: ${data.location}`)
        this.updateUI('granted')
      } else {
        this.showError(data.error || 'Failed to update location')
      }
    })
    .catch(error => {
      console.error('Location update error:', error)
      this.showError('Failed to save location')
    })
  }
  
  handleError(error) {
    let message
    switch(error.code) {
      case error.PERMISSION_DENIED:
        message = "Location access denied. Please enable location services."
        this.updateUI('denied')
        break
      case error.POSITION_UNAVAILABLE:
        message = "Location information unavailable."
        break
      case error.TIMEOUT:
        message = "Location request timed out."
        break
      default:
        message = "An unknown error occurred while retrieving location."
        break
    }
    
    this.showError(message)
    this.updateUI('error')
  }
  
  updateUI(state) {
    if (!this.hasStatusTarget) return
    
    const statusEl = this.statusTarget
    const buttonEl = this.hasButtonTarget ? this.buttonTarget : null
    
    switch(state) {
      case 'granted':
        statusEl.innerHTML = '<i class="fas fa-check-circle text-green-500 mr-2"></i>Location shared'
        statusEl.className = 'text-sm text-green-600'
        if (buttonEl) {
          buttonEl.innerHTML = '<i class="fas fa-map-marker-alt mr-2"></i>Update Location'
          buttonEl.disabled = false
        }
        break
        
      case 'denied':
        statusEl.innerHTML = '<i class="fas fa-times-circle text-red-500 mr-2"></i>Location access denied'
        statusEl.className = 'text-sm text-red-600'
        if (buttonEl) {
          buttonEl.innerHTML = '<i class="fas fa-map-marker-alt mr-2"></i>Request Location'
          buttonEl.disabled = false
        }
        break
        
      case 'loading':
        statusEl.innerHTML = '<i class="fas fa-spinner fa-spin text-blue-500 mr-2"></i>Getting location...'
        statusEl.className = 'text-sm text-blue-600'
        if (buttonEl) {
          buttonEl.disabled = true
        }
        break
        
      case 'error':
        statusEl.innerHTML = '<i class="fas fa-exclamation-triangle text-yellow-500 mr-2"></i>Location error'
        statusEl.className = 'text-sm text-yellow-600'
        if (buttonEl) {
          buttonEl.innerHTML = '<i class="fas fa-map-marker-alt mr-2"></i>Retry Location'
          buttonEl.disabled = false
        }
        break
        
      default: // 'prompt' or unknown
        statusEl.innerHTML = '<i class="fas fa-map-marker-alt text-gray-400 mr-2"></i>Location not shared'
        statusEl.className = 'text-sm text-gray-600'
        if (buttonEl) {
          buttonEl.innerHTML = '<i class="fas fa-map-marker-alt mr-2"></i>Share Location'
          buttonEl.disabled = false
        }
    }
  }
  
  showSuccess(message) {
    this.showMessage(message, 'success')
  }
  
  showError(message) {
    this.showMessage(message, 'error')
  }
  
  updateLocationDisplay(locationText) {
    if (!this.hasStatusTarget) return
    
    const statusEl = this.statusTarget
    statusEl.innerHTML = `<i class="fas fa-check-circle text-green-500 mr-1"></i>${locationText}`
    statusEl.className = 'text-xs text-green-600'
    
    // Update button text
    if (this.hasButtonTarget) {
      this.buttonTarget.innerHTML = '<i class="fas fa-crosshairs mr-1"></i>GPS'
      this.buttonTarget.disabled = false
    }
  }
  
  showMessage(message, type) {
    // Create toast-like notification
    const toast = document.createElement('div')
    const bgColor = type === 'success' ? 'bg-green-500' : 'bg-red-500'
    
    toast.className = `fixed top-20 right-4 ${bgColor} text-white px-4 py-3 rounded-lg shadow-lg z-50 max-w-sm`
    toast.innerHTML = `
      <div class="flex items-center">
        <i class="fas fa-${type === 'success' ? 'check' : 'exclamation-triangle'} mr-2"></i>
        <span class="text-sm">${message}</span>
      </div>
    `
    
    document.body.appendChild(toast)
    
    // Auto remove after 5 seconds
    setTimeout(() => {
      toast.remove()
    }, 5000)
  }
}